import {Tabs, Box, Text, Collapsible} from '@chakra-ui/react'
import {LuChevronDown, LuDroplet, LuDroplets, LuFlaskConical, LuPackage, LuReceiptText} from 'react-icons/lu'
import {AggregatedCollapsible} from '../../../aggregated-collapsible'
import {useTranslate} from '@tolgee/react'
import {MappedBatches, MappedRestVials} from '@cypro/engine/utils'
import {ReturnItem} from '@cypro/engine/schema'
import {ResourceDrawerSummary} from '@/shared/components/ui/resource-drawer-summary'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {ReturnRow} from '../../form/return-row'
import {SolventRequirementsList} from '../../solvent-requirements-list'
import type {SolventRequirement} from '../../utils'

export function AggregatedPickListTab({
  mappedBatches,
  mappedRestVials,
  mappedReturns,
  solventRequirements,
}: {
  mappedBatches: MappedBatches[]
  mappedRestVials: MappedRestVials[]
  mappedReturns: ReturnItem[]
  /** set when the solvent stock check is skipped: solvents are listed by amount instead of by batch */
  solventRequirements?: SolventRequirement[]
}) {
  const {t} = useTranslate('common')
  const mappedActiveSubstance = mappedBatches.filter(b => b.type === 'activeSubstances')
  const mappedCarrier = mappedBatches.filter(b => b.type === 'carrier')
  const mappedSolvent = mappedBatches.filter(b => b.type === 'solvents')
  const mappedConsumables = mappedBatches.filter(b => b.type === 'consumables')
  return (
    <Tabs.Content value="aggregated" spaceY="3" display="flex" flexDirection="column">
      <Box display="flex" flexDirection="column" pb={1}>
        <Text fontSize="md" fontWeight="medium">
          {t('picking_list.view_by_aggregated')}
        </Text>
        <Text fontSize="sm" fontWeight="normal" color="fg.muted">
          {t('picking_list.aggregated_description')}
        </Text>
      </Box>
      {mappedReturns?.length > 0 && (
        <Collapsible.Root bg="gray.50" borderRadius="md" borderWidth="1px">
          <Collapsible.Trigger
            paddingInline="3"
            width="full"
            display="flex"
            gap="2"
            alignItems="center"
            justifyContent={'space-between'}
          >
            <ResourceDrawerSummary
              title={t('pharmacy_return')}
              description={`${mappedReturns.length} items`}
              icon={LuReceiptText}
            />
            <Collapsible.Indicator transition="transform 0.2s" _open={{transform: 'rotate(180deg)'}}>
              <LuChevronDown />
            </Collapsible.Indicator>
          </Collapsible.Trigger>
          <Collapsible.Content padding="3">
            <FormSection variant="card" width={'100%'} bg="white">
              {mappedReturns.map(ret => (
                <ReturnRow key={ret.returnId} returnItem={ret} />
              ))}
            </FormSection>
          </Collapsible.Content>
        </Collapsible.Root>
      )}
      {(mappedActiveSubstance.length > 0 || mappedRestVials.length > 0) && (
        <AggregatedCollapsible
          title={t('active_substances')}
          icon={LuFlaskConical}
          type="activeSubstances"
          mappedBatches={mappedActiveSubstance}
          mappedRestVials={mappedRestVials}
        />
      )}
      {mappedCarrier.length > 0 && (
        <AggregatedCollapsible
          title={t('carrier_solutions')}
          icon={LuDroplets}
          type="carrier"
          mappedBatches={mappedCarrier}
        />
      )}
      {solventRequirements && solventRequirements.length > 0 && (
        <SolventRequirementsList requirements={solventRequirements} />
      )}
      {!solventRequirements && mappedSolvent.length > 0 && (
        <AggregatedCollapsible
          title={t('solvents')}
          icon={LuDroplet}
          type="solvents"
          mappedBatches={mappedSolvent}
        />
      )}
      {mappedConsumables.length > 0 && (
        <AggregatedCollapsible
          title={t('consumables')}
          icon={LuPackage}
          type="consumables"
          mappedBatches={mappedConsumables}
        />
      )}
    </Tabs.Content>
  )
}
